import type {
  ExtractionApiResponse,
  ExtractionTypeOption,
} from './AIVoiceExtraction.types';

export type VoiceDocumentType =
  | 'medical_history'
  | 'embryo_grading'
  | 'oocyte_grading';

type SaveVoiceExtractionParams = {
  patientId: number | string;
  transcriptionId: number;
  emrId: number;
  documentType: VoiceDocumentType;
  emrData: Record<string, unknown>;
};

type SaveSummaryParams = {
  patientId: number | string;
  aiNotes: string;
  duration?: string;
  type?: string;
  document_name?: string;
  document_type?: string;
  doctor_name?: string;
  source?: string;
  call_trascript?: string;
};

export type ActiveCycleCheckResult = {
  hasActiveCycle: boolean;
  raw: unknown;
};

// const API_BASE_URL = 'http://192.168.10.156:8000/api'.replace(
//   /\/+$/,
//   ''
// );
const API_BASE_URL = 'https://99999.preview-api.vidaisolutions.com/api'.replace(
  /\/+$/,
  ''
);

export const SAVE_API_CONFIG = {
  saveVoicePath: (patientId: number | string, documentType: VoiceDocumentType) =>
    `ai-components/save-voice/${patientId}/?document_type=${encodeURIComponent(
      documentType
    )}`,
  saveSummaryPath: (patientId: number | string) =>
    `patients/${patientId}/summary-save/`,
  checkActiveCyclePath: (patientId: number | string) =>
    `cycle/is-cycle-active/?patient_id=${patientId}`,
};

const buildApiUrl = (path: string) => {
  const cleanedPath = path.replace(/^\/+/, '');
  return API_BASE_URL ? `${API_BASE_URL}/${cleanedPath}` : `/${cleanedPath}`;
};

const getAuthToken = () => {
  return (
    localStorage.getItem('token') ||
    localStorage.getItem('accessToken') ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('token') ||
    sessionStorage.getItem('accessToken') ||
    ''
  );
};

const buildHeaders = (): HeadersInit => {
  const token = getAuthToken();

  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token
      ? {
        Authorization: token.startsWith('Bearer ')
          ? token
          : `Bearer ${token}`,
      }
      : {}),
  };
};

const extractErrorMessage = async (response: Response) => {
  try {
    const rawText = await response.text();

    if (!rawText) {
      return `Request failed with status ${response.status}`;
    }

    try {
      const parsed = JSON.parse(rawText);
      return (
        parsed?.detail ||
        parsed?.message ||
        parsed?.error ||
        parsed?.errors?.[0] ||
        rawText
      );
    } catch {
      return rawText;
    }
  } catch {
    return `Request failed with status ${response.status}`;
  }
};

const requestJson = async <T>(
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: {
      ...buildHeaders(),
      ...(options.headers ?? {}),
    },
  });

  if (!response.ok) {
    throw new Error(await extractErrorMessage(response));
  }

  const text = await response.text();

  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return text as T;
  }
};

export const resolveDocumentType = (
  option: ExtractionTypeOption | null
): VoiceDocumentType | null => {
  if (!option) return null;

  const normalized = `${option.key} ${option.label} ${option.description ?? ''}`
    .toLowerCase()
    .replace(/[_-]+/g, ' ');

  if (normalized.includes('medical') || normalized.includes('history')) {
    return 'medical_history';
  }

  if (normalized.includes('embryo')) {
    return 'embryo_grading';
  }

  if (normalized.includes('oocyte')) {
    return 'oocyte_grading';
  }

  return null;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

const ACTIVE_STATUSES = new Set([
  'active',
  'ongoing',
  'in_progress',
  'in-progress',
  'open',
  'running',
  'current',
]);

const isActiveStatus = (value: unknown) =>
  typeof value === 'string' &&
  ACTIVE_STATUSES.has(value.trim().toLowerCase());

const objectHasActiveCycleFlag = (record: Record<string, unknown>) => {
  const booleanCandidates = [
    record.has_active_cycle,
    record.hasActiveCycle,
    record.active_cycle,
    record.activeCycle,
    record.is_active_cycle,
    record.isActiveCycle,
    record.active,
    record.is_active,
    record.isActive,
  ];

  for (const candidate of booleanCandidates) {
    if (typeof candidate === 'boolean') {
      return candidate;
    }
  }

  const statusCandidates = [
    record.status,
    record.cycle_status,
    record.cycleStatus,
    asRecord(record.cycle)?.status,
    asRecord(record.data)?.status,
    asRecord(asRecord(record.data)?.cycle)?.status,
  ];

  for (const candidate of statusCandidates) {
    if (isActiveStatus(candidate)) {
      return true;
    }
  }

  return null;
};

const arrayHasActiveCycle = (items: unknown[]) => {
  if (!items.length) return false;

  for (const item of items) {
    const record = asRecord(item);
    if (!record) continue;

    const objectResult = objectHasActiveCycleFlag(record);
    if (objectResult === true) return true;
  }

  return items.length > 0;
};

const parseHasActiveCycle = (payload: unknown): boolean => {
  if (typeof payload === 'boolean') return payload;

  if (Array.isArray(payload)) {
    return arrayHasActiveCycle(payload);
  }

  const record = asRecord(payload);
  if (!record) return false;

  const directFlag = objectHasActiveCycleFlag(record);
  if (typeof directFlag === 'boolean') {
    return directFlag;
  }

  const nestedArrays = [
    record.results,
    record.objects,
    record.cycles,
    record.items,
    asRecord(record.data)?.results,
    asRecord(record.data)?.objects,
    asRecord(record.data)?.cycles,
    asRecord(record.data)?.items,
  ];

  for (const candidate of nestedArrays) {
    if (Array.isArray(candidate)) {
      return arrayHasActiveCycle(candidate);
    }
  }

  const nestedCycleObjects = [
    record.cycle,
    asRecord(record.data)?.cycle,
    record.data,
  ];

  for (const candidate of nestedCycleObjects) {
    const nestedRecord = asRecord(candidate);
    if (!nestedRecord) continue;

    const result = objectHasActiveCycleFlag(nestedRecord);
    if (typeof result === 'boolean') {
      return result;
    }
  }

  return false;
};

export const normalizeEmrDataForDocumentType = (
  documentType: VoiceDocumentType,
  data: Record<string, unknown>
): Record<string, unknown> => {
  const rootRecord = asRecord(data.emr_data) ?? data;

  if (documentType === 'medical_history') {
    if (rootRecord.medical_history) {
      const nested = asRecord(rootRecord.medical_history);
      return nested ?? rootRecord;
    }

    return rootRecord;
  }

  if (documentType === 'embryo_grading') {
    if (rootRecord.embryo_culture) {
      return rootRecord;
    }

    return {
      embryo_culture: rootRecord,
    };
  }

  if (documentType === 'oocyte_grading') {
    if (rootRecord.oocyte_grading) {
      return rootRecord;
    }

    return {
      oocyte_grading: rootRecord,
    };
  }

  return rootRecord;
};

export const buildSummaryAiNotes = ({
  option,
  documentType,
  summaryHeading,
  summaryLines,
  extractionResponse,
  transcriptionId,
  emrId,
  editedStructuredData,
  saveContext,
  cycleValidation,
}: {
  option: ExtractionTypeOption;
  documentType: VoiceDocumentType;
  summaryHeading: string;
  summaryLines: string[];
  extractionResponse: ExtractionApiResponse | Record<string, unknown>;
  transcriptionId?: number | null;
  emrId?: number | null;
  editedStructuredData?: Record<string, unknown> | null;
  saveContext?: 'manual_summary_save' | 'no_active_cycle_fallback';
  cycleValidation?: {
    checked: boolean;
    hasActiveCycle: boolean | null;
  };
}) => {
  return JSON.stringify({
    source: 'ai_voice_extraction',
    document_type: documentType,
    extraction_type: {
      key: option.key,
      label: option.label,
      description: option.description,
    },
    transcription_id: transcriptionId ?? null,
    emr_id: emrId ?? null,
    summary_heading: summaryHeading,
    summary_lines: summaryLines,
    parser_response: extractionResponse,
    edited_structured_data: editedStructuredData ?? null,
    save_context: saveContext ?? 'manual_summary_save',
    cycle_validation: cycleValidation ?? null,
    created_at: new Date().toISOString(),
  });
};

export const checkPatientHasActiveCycle = async (
  patientId: number | string
): Promise<ActiveCycleCheckResult> => {
  const raw = await requestJson<unknown>(
    SAVE_API_CONFIG.checkActiveCyclePath(patientId),
    {
      method: 'GET',
    }
  );

  return {
    hasActiveCycle: parseHasActiveCycle(raw),
    raw,
  };
};

export const saveVoiceExtraction = async (
  params: SaveVoiceExtractionParams
) => {
  const payload = {
    transcription_id: params.transcriptionId,
    emr_id: params.emrId,
    emr_data: params.emrData,
  };

  return requestJson(
    SAVE_API_CONFIG.saveVoicePath(params.patientId, params.documentType),
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
};

const normalizeDuration = (duration: unknown): string => {
  // Default to 0 seconds if duration is nullish
  if (duration == null || duration === '') {
    return '00 : 00 : 00';
  }

  if (typeof duration === 'number' && Number.isFinite(duration)) {
    const totalSeconds = Math.max(0, Math.floor(duration));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (value: number) => value.toString().padStart(2, '0');

    return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
  }

  // For non-empty strings or other types, pass through as-is.
  return String(duration);
};

export const saveSummaryNotes = async (params: SaveSummaryParams) => {
  return requestJson(SAVE_API_CONFIG.saveSummaryPath(params.patientId), {
    method: 'POST',
    body: JSON.stringify({
      ai_notes: params.aiNotes,
      duration: normalizeDuration(params.duration),
      type: params.type ?? 'voice_to_text',
      document_name: params.document_name ?? '',
      document_type: params.document_type ?? '',
      doctor_name: params.doctor_name ?? '',
      source: params.source ?? '',
      call_trascript: params.call_trascript ?? '',
    }),
  });
};